import EventEmitter from 'eventemitter3';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  type ProtocolConnection,
  WorkDoneProgress,
} from 'vscode-languageserver-protocol/browser';

import {
  CanComposeNotification,
  ConfigureAutoComposition,
  RequestCompositionRequest,
} from './customEvents';
let isComposing = false;
function updateIsComposing(value: boolean) {
  isComposing = value;
  eventEmitter.emit('isComposingChange');
}

let invalidSubgraphs: string[] = [];
function updateInvalidSubgraphs(value: string[]) {
  invalidSubgraphs = value;
  eventEmitter.emit('invalidSubgraphsChanged');
}

let cachedConnection: ProtocolConnection | undefined;
let cachedConnectionCleanup: (() => void) | undefined;
export function setCachedConnection(connection: ProtocolConnection) {
  cachedConnectionCleanup?.();
  cachedConnection = connection;
  const disposables = [
    cachedConnection.onProgress(
      WorkDoneProgress.type,
      'apollo/composition',
      (value) => {
        switch (value.kind) {
          case 'begin':
            updateIsComposing(true);
            break;
          case 'report':
            updateIsComposing(true);
            break;
          case 'end':
            updateIsComposing(false);
            break;
          default:
            break;
        }
      },
    ),
    cachedConnection.onNotification(CanComposeNotification.type, (data) => {
      updateInvalidSubgraphs(data.subgraphsWithErrors);
    }),
  ];
  cachedConnectionCleanup = () => {
    disposables.forEach((d) => d.dispose());
  };
}

let autoComposition = false;
async function setAutoComposition(enabled: boolean) {
  autoComposition = enabled;
  eventEmitter.emit('autoCompositionChange');
  await cachedConnection?.sendNotification(ConfigureAutoComposition.type, {
    enabled,
  });
}

function getActions() {
  return {
    isComposing,
    triggerComposition,
    autoComposition,
    setAutoComposition,
    invalidSubgraphs,
  };
}

const eventEmitter = new EventEmitter<
  'isComposingChange' | 'autoCompositionChange' | 'invalidSubgraphsChanged'
>();
const ApolloGraphqlContext = createContext<ReturnType<
  typeof getActions
> | null>(null);

async function triggerComposition() {
  if (!cachedConnection) return;
  updateIsComposing(true);
  const compositionStarted = await cachedConnection.sendRequest(
    RequestCompositionRequest.type,
    {},
  );

  if (compositionStarted.didCompose === false) {
    // if we didn't compose, we don't get a end progress, so set this back to false
    updateIsComposing(false);
  }
}

export function useApolloGraphqlActions() {
  const autoCompositionEnabled = useContext(ApolloGraphqlContext);
  if (!autoCompositionEnabled) {
    throw new Error(
      `useApolloGraphqlActions must be used within an ApolloGraphqlActionsProvider`,
    );
  }
  return autoCompositionEnabled;
}

export function ApolloGraphqlActionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [value, setValue] = useState(getActions);
  useEffect(() => {
    const handleChange = () => setValue(getActions());
    eventEmitter.on('autoCompositionChange', handleChange);
    eventEmitter.on('isComposingChange', handleChange);
    eventEmitter.on('invalidSubgraphsChanged', handleChange);
    return () => {
      eventEmitter.off('autoCompositionChange', handleChange);
      eventEmitter.off('isComposingChange', handleChange);
      eventEmitter.off('invalidSubgraphsChanged', handleChange);
    };
  }, []);

  return (
    <ApolloGraphqlContext.Provider value={value}>
      {children}
    </ApolloGraphqlContext.Provider>
  );
}
